// Player insights (D-075): an ontology over the relational tables so admins can read the player network as a graph,
// and a privacy-safe usage log (which screens and controls are used, and where on the page people tap).
//
// The ontology is the single description of what a node and a relation mean. The server builds the graph from it
// (insights/graph.ts reads each relation's source table), the admin web colours and labels relations from it, and
// nothing else in the app needs to know about "edges".
import { z } from "zod";
/* ---------- Ontology ---------- */
export const NODE_KINDS = ["player", "club"];
export const RELATIONS = ["played", "boli", "referred", "invited", "friends", "member", "blocked"];
export const ONTOLOGY = {
    played: { id: "played", label: "Played with", verb: "played with", from: "player", to: "player", directed: false, unit: ["round together", "rounds together"], source: "Rounds at private, online, ranked, club and tournament tables with no computers (head_to_head; pair_days for dated windows).", dated: true, defaultOn: true },
    boli: { id: "boli", label: "Sent Boli", verb: "sent Boli to", from: "player", to: "player", directed: true, unit: ["Boli", "Boli"], source: "Recorded Boli transfers (transfers).", dated: true, defaultOn: true },
    referred: { id: "referred", label: "Brought in", verb: "brought in", from: "player", to: "player", directed: true, unit: ["sign-up", "sign-ups"], source: "Accounts created with another player's invite code (accounts.referred_by).", dated: true, defaultOn: true },
    invited: { id: "invited", label: "Invited to a table", verb: "invited", from: "player", to: "player", directed: true, unit: ["invite", "invites"], source: "Table invites sent from a lobby (invite_log, kept from 25 October 2026).", dated: true, defaultOn: true },
    friends: { id: "friends", label: "Friends", verb: "is friends with", from: "player", to: "player", directed: false, unit: ["friendship", "friendships"], source: "Accepted friend requests (friend_pairs).", dated: true, defaultOn: false },
    member: { id: "member", label: "Club member", verb: "is a member of", from: "player", to: "club", directed: true, unit: ["membership", "memberships"], source: "Club members (club_members).", dated: true, defaultOn: false },
    blocked: { id: "blocked", label: "Blocked", verb: "blocked", from: "player", to: "player", directed: true, unit: ["block", "blocks"], source: "Blocks between players (blocks).", dated: true, defaultOn: false },
};
export const GraphQuery = z.object({
    rels: z.string().optional(),
    focus: z.string().optional(),
    depth: z.coerce.number().int().min(1).max(3).default(2),
    days: z.coerce.number().int().min(0).max(3650).default(0),
    min: z.coerce.number().int().min(1).max(1000).default(1),
    limit: z.coerce.number().int().min(10).max(800).default(300),
});
/* ---------- Usage tracking ---------- */
/**
 * What the client records. Never what anyone typed or said: a click is the control's stable id (`data-track`) or a
 * short, cleaned label, the route with its codes replaced by placeholders, and where on the page it happened.
 */
export const TrackEvent = z.object({
    kind: z.enum(["view", "click"]),
    route: z.string().min(1).max(120),
    /** Stable control id (`data-track`) or the cleaned label. */
    target: z.string().max(60).optional(),
    /** Fraction of the viewport width (0–1) and page Y in CSS pixels. */
    x: z.number().min(0).max(1).optional(),
    y: z.number().min(0).max(100_000).optional(),
    vw: z.number().int().min(200).max(8000),
    vh: z.number().int().min(200).max(8000),
    /** Page height at the time (for heatmaps). */
    dh: z.number().int().min(0).max(100_000).optional(),
    at: z.number().int(),
});
export const TrackBatch = z.object({ events: z.array(TrackEvent).min(1).max(60) });
export const deviceOf = (vw) => (vw < 900 ? "phone" : "desktop");
/** Routes with their codes and ids replaced, so every club page counts as one screen. */
export function normaliseRoute(path) {
    const p = (path.split(/[?#]/)[0] ?? "/").replace(/\/+$/, "") || "/";
    return p
        .replace(/\/CL-[A-F0-9]{6,}/gi, "/:club")
        .replace(/\/DG-[A-Za-z0-9]{6,}/g, "/:player")
        .replace(/\/[a-f0-9]{24,}/gi, "/:id")
        .replace(/\/(table|lobby|results|review|archive|invites)\/[^/]+/g, "/$1/:code")
        .replace(/\/rooms\/(?!join(?:\/|$))[^/]+/g, "/rooms/:code")
        .replace(/\/(players|people|send|transfer)\/[^/]+/g, "/$1/:player")
        .replace(/\/tables\/[A-Z0-9]{6,}/g, "/tables/:code")
        .replace(/\/\d+(?=\/|$)/g, "/:n");
}
export const FEATURES = [
    { id: "play-online", label: "Play online", targets: ["tile-public"] },
    { id: "ranked", label: "Ranked", targets: ["tile-season"] },
    { id: "private", label: "Private table", routes: ["/setup"] },
    { id: "practice", label: "Practice", routes: ["/practice"] },
    { id: "join-code", label: "Join with a code", targets: ["home-join"] },
    { id: "time-bank", label: "Time Bank", targets: ["add-time"] },
    { id: "chat", label: "Table chat", targets: ["chat-button"] },
    { id: "reactions", label: "Reactions", targets: ["reaction"] },
    { id: "table-shop", label: "Table shop", targets: ["shop-button"] },
    { id: "live-results", label: "Live results", targets: ["results-button"] },
    { id: "about-table", label: "About this table", targets: ["plaque"] },
    { id: "sort", label: "Sort hand", targets: ["sort-pill"] },
    { id: "review", label: "Round review", routes: ["/review/:code"] },
    { id: "invite-friends", label: "Invite to a table", targets: ["invite-friend"] },
    { id: "watch", label: "Watch a club table", targets: ["club-watch"] },
    { id: "queue", label: "Seat queue", targets: ["seat-queue"] },
    { id: "clubs", label: "Clubs", routes: ["/clubs", "/clubs/:club"] },
    { id: "tournaments", label: "Tournaments", routes: ["/tournaments", "/tournaments/:id"] },
    { id: "friends", label: "Friends", routes: ["/friends", "/friends/add"] },
    { id: "shop", label: "Shop", routes: ["/shop"] },
    { id: "send-boli", label: "Send Boli", routes: ["/send", "/send/:player"] },
    { id: "leaderboard", label: "Leaderboard", routes: ["/leaderboard"] },
    { id: "themes", label: "Appearance", routes: ["/you/settings/appearance"] },
];
//# sourceMappingURL=insights.js.map